import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export interface WindowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FloatingWindowOptions {
  /** Where the window sits the very first time it is opened in this browser. */
  defaultRect: (viewport: { width: number; height: number }) => WindowRect;
  minWidth: number;
  minHeight: number;
  /**
   * The size actually on screen right now. A collapsed window is sized by its
   * content rather than by `rect`, so clamping cannot read the size back out
   * of the stored rectangle.
   */
  clampSize?: { width: number; height: number };
}

const MARGIN = 12;

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight };
}

function clampPosition(rect: WindowRect, size: { width: number; height: number }): WindowRect {
  const maxX = Math.max(MARGIN, window.innerWidth - size.width - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - size.height - MARGIN);
  return {
    ...rect,
    x: Math.min(Math.max(rect.x, MARGIN), maxX),
    y: Math.min(Math.max(rect.y, MARGIN), maxY),
  };
}

function load(key: string): WindowRect | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<WindowRect>;
    if (
      typeof parsed.x === 'number' &&
      typeof parsed.y === 'number' &&
      typeof parsed.width === 'number' &&
      typeof parsed.height === 'number'
    ) {
      return { x: parsed.x, y: parsed.y, width: parsed.width, height: parsed.height };
    }
  } catch {
    // Corrupt or blocked storage — the default placement is a fine fallback.
  }
  return null;
}

function save(key: string, rect: WindowRect): void {
  try {
    localStorage.setItem(key, JSON.stringify(rect));
  } catch {
    // Non-fatal: the window just reopens at its default spot next time.
  }
}

/**
 * A real window rather than a docked panel: it is dragged by its title bar,
 * resized from its bottom-right corner, and remembers both per browser.
 *
 * Placement is the user's call on purpose — an infinite canvas has no corner
 * that is out of the way for everyone — so the only thing enforced here is
 * that a window can never strand itself off-screen after a viewport resize.
 */
export function useFloatingWindow(storageKey: string, options: FloatingWindowOptions) {
  const { minWidth, minHeight } = options;
  const [rect, setRect] = useState<WindowRect>(() => load(storageKey) ?? options.defaultRect(viewport()));
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);

  const gesture = useRef<{ startX: number; startY: number; origin: WindowRect } | null>(null);
  const clampSize = options.clampSize ?? { width: rect.width, height: rect.height };
  const clampRef = useRef(clampSize);
  clampRef.current = clampSize;

  useEffect(() => {
    const onResize = () => setRect((r) => clampPosition(r, clampRef.current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const begin = useCallback(
    (e: ReactPointerEvent) => {
      (e.target as Element).setPointerCapture(e.pointerId);
      gesture.current = { startX: e.clientX, startY: e.clientY, origin: rect };
    },
    [rect],
  );

  const end = useCallback(() => {
    if (!gesture.current) return;
    gesture.current = null;
    setDragging(false);
    setResizing(false);
    setRect((r) => {
      save(storageKey, r);
      return r;
    });
  }, [storageKey]);

  const onDragStart = useCallback(
    (e: ReactPointerEvent) => {
      begin(e);
      setDragging(true);
    },
    [begin],
  );

  const onDragMove = useCallback((e: ReactPointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    setRect((r) =>
      clampPosition(
        { ...r, x: g.origin.x + (e.clientX - g.startX), y: g.origin.y + (e.clientY - g.startY) },
        clampRef.current,
      ),
    );
  }, []);

  const onResizeStart = useCallback(
    (e: ReactPointerEvent) => {
      e.stopPropagation();
      begin(e);
      setResizing(true);
    },
    [begin],
  );

  const onResizeMove = useCallback(
    (e: ReactPointerEvent) => {
      const g = gesture.current;
      if (!g) return;
      setRect((r) => ({
        ...r,
        width: Math.min(
          Math.max(g.origin.width + (e.clientX - g.startX), minWidth),
          window.innerWidth - r.x - MARGIN,
        ),
        height: Math.min(
          Math.max(g.origin.height + (e.clientY - g.startY), minHeight),
          window.innerHeight - r.y - MARGIN,
        ),
      }));
    },
    [minWidth, minHeight],
  );

  return {
    rect,
    dragging,
    resizing,
    dragHandlers: { onPointerDown: onDragStart, onPointerMove: onDragMove, onPointerUp: end },
    resizeHandlers: { onPointerDown: onResizeStart, onPointerMove: onResizeMove, onPointerUp: end },
  };
}
